/**
 * The system picture-in-picture window of a call on the phone (Robert,
 * 2026-10-07: "is picture in picture when not actively in the app supposed
 * to work? Still doesn't"). These are the pure decisions: whose video goes
 * into the window, and the shape of the window. The stage order comes from
 * VRSLayout.native.tsx (orderPanesForDisplay, orderVriPanesForDisplay).
 */
/**
 * The person whose video goes into the system window. It is the first remote
 * person in the stage order, after the viewer's own arrangement of the tiles
 * (tileOrder.ts: drag a person into the main spot and the window follows):
 * the largest picture, also after a swap made their picture the small one.
 * If that person shows no video, it is the next remote person with video. It
 * is never your own video: the camera stops in the background.
 *
 * One person at a time: only that person's video view gets the window.
 *
 * @param {Array} candidates - Everyone on the stage in the stage order, the
 * floating tile too.
 * @returns {string|undefined}
 */
export function systemPipParticipantId(candidates) {
    return candidates
        .find(candidate => !candidate.local && candidate.participantId && candidate.hasVideo)
        ?.participantId;
}
/**
 * The person in the window while the call is not on screen: the app is in
 * the background, or the window shows over Home (the down arrow,
 * callMinimize.ts). The window is one video view; if the choice moved to
 * another person, that view would go and the window would close. So while
 * the call is away, the person already in the window stays there as long as
 * they are in the call, also while their video pauses. When the call shows
 * again, the choice follows the stage again.
 *
 * @param {Object} input - The inputs.
 * @param {boolean} input.appActive - The app is on screen.
 * @param {string|undefined} input.chosen - systemPipParticipantId now.
 * @param {string|undefined} input.held - The person in the window when the
 * call went away.
 * @param {string[]} input.present - Everyone on the stage now.
 * @param {boolean} [input.windowOpen] - The iOS window shows now.
 * @returns {string|undefined}
 */
export function holdSystemPipParticipant({ appActive, chosen, held, present, windowOpen = false }) {
    if ((!appActive || windowOpen) && held && present.includes(held)) {
        return held;
    }
    return chosen;
}
/**
 * The widest window, as width over height. Android accepts a window from
 * 1:2.39 to 2.39:1 and refuses a shape outside that range. This limit keeps
 * a margin.
 */
export const SYSTEM_PIP_MAX_ASPECT = 2.35;
/**
 * The shape of the Android window for a video of the given size: the shape
 * of the video, inside the range Android accepts. Nothing while the size is
 * not known.
 *
 * @param {number} width - The video width the renderer reported.
 * @param {number} height - The video height the renderer reported.
 * @returns {{ height: number, width: number }|undefined}
 */
export function systemPipAspect(width, height) {
    if (!(typeof width === 'number' && Number.isFinite(width) && width > 0
        && typeof height === 'number' && Number.isFinite(height) && height > 0)) {
        return undefined;
    }
    const ratio = width / height;
    if (ratio > SYSTEM_PIP_MAX_ASPECT) {
        return {
            height: 100,
            width: Math.round(SYSTEM_PIP_MAX_ASPECT * 100)
        };
    }
    if (ratio < 1 / SYSTEM_PIP_MAX_ASPECT) {
        return {
            height: Math.round(SYSTEM_PIP_MAX_ASPECT * 100),
            width: 100
        };
    }
    return {
        height: Math.max(1, Math.round(height)),
        width: Math.max(1, Math.round(width))
    };
}
